import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useProgram } from '../../context/ProgramContext';
import apiClient from '../../utils/api';
import AppHeader from '../Header/AppHeader';
import '../CustomDialog.css';
import './ProgramsView.css';

function ProgramsView() {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language === 'de';
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('alle');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  
  const {
    runningProgram,
    currentStep,
    timeRemaining,
    isPaused,
    startProgram,
    stopProgram,
    togglePause,
  } = useProgram();

  // Map German program names to i18n keys
  const programKeyMap = {
    'Morgen-Energie': 'morningEnergy',
    'Abend-Entspannung': 'eveningRelaxation',
    'Fokus-Flow': 'focusFlow',
    'Tiefen-Heilung': 'deepHealing',
    'Stress-Reset': 'stressReset',
    'Kreativ-Boost': 'creativeBoost',
    'Schlaf-Vorbereitung': 'sleepPreparation',
    'Energie-Aktivierung': 'energyActivation',
    'Schutz & Klarheit': 'protectionClarity',
    'Intuition & Natur': 'intuitionNature',
    'Herzöffnung': 'heartOpening',
    'Fülle & Manifestation': 'abundanceManifestation',
    'Vollkommene Balance': 'perfectBalance',
    'Manifestations-Ritual': 'manifestationRitual',
    'Mondphasen-Transformation': 'moonPhaseTransformation',
    'Energie-Meisterschaft': 'energyMastery',
    'Tiefenheilung Intensiv': 'deepHealingIntensive',
    'Natur-Weisheit-Flow': 'natureWisdomFlow'
  };

  // Map German category names to i18n keys
  const categoryKeyMap = {
    'alle': 'all',
    'Produktivität': 'productivity',
    'Entspannung': 'relaxation',
    'Heilung': 'healing',
    'Schlaf': 'sleep',
    'Energie': 'energy',
    'Kreativität': 'creativity'
  };

  useEffect(() => {
    loadPrograms();
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      const data = await apiClient.getSubscriptionStatus();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };

  const loadPrograms = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getPrograms();
      setPrograms(data);
    } catch (error) {
      console.error('Error loading programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTotalDuration = (program) => {
    return program.steps.reduce((sum, step) => sum + step.duration_minutes, 0);
  };

  const translateProgramName = (name) => {
    const key = programKeyMap[name];
    if (key) {
      return t(`programs.list.${key}.name`);
    }
    return name;
  };

  const translateProgramDescription = (program) => {
    const key = programKeyMap[program.name];
    if (key) {
      return t(`programs.list.${key}.description`);
    }
    return program.description;
  };

  const translateCategory = (category) => {
    const key = categoryKeyMap[category];
    if (key) {
      return t(`programs.categories.${key}`);
    }
    return category;
  };

  const handleProgramClick = (program) => {
    // Check if program is premium and user is not premium
    const isPremiumProgram = program.is_premium;
    const userIsPremium = subscriptionStatus?.tier === 'premium';
    
    if (isPremiumProgram && !userIsPremium) {
      setShowPremiumModal(true);
      return;
    }
    
    // Start program
    startProgram(program);
  };

  const categories = ['alle', ...new Set(programs.map(p => p.category))];
  const filteredPrograms = selectedCategory === 'alle' 
    ? programs 
    : programs.filter(p => p.category === selectedCategory);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <>
      <AppHeader subscriptionStatus={subscriptionStatus} />
      <div className="dashboard-container programs-dashboard">
        <main className="dashboard-main programs-main">
        {runningProgram && (
          <div className="program-runner">
            <div className="runner-header">
              <div className="runner-title">
                <span className="runner-icon">{runningProgram.icon}</span>
                <span className="runner-name">{translateProgramName(runningProgram.name)}</span>
              </div>
              <button onClick={stopProgram} className="btn-stop" data-testid="stop-program">
                ⏹️ {t('programs.stop')}
              </button>
            </div>

            <div className="runner-progress">
              <div className="progress-steps">
                {runningProgram.steps.map((step, index) => (
                  <div
                    key={index}
                    className={`progress-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                  >
                    <div className="step-number">{index + 1}</div>
                    <div className="step-icon" style={{ color: step.frequency_color }}>
                      {step.frequency_icon}
                    </div>
                    <div className="step-name">{step.frequency_name}</div>
                    <div className="step-duration">{step.duration_minutes} {t('programs.min')}</div>
                  </div>
                ))}
              </div>

              <div className="current-step-details">
                <div 
                  className="current-step-card"
                >
                  <div className="current-step-icon" style={{ color: runningProgram.steps[currentStep].frequency_color }}>
                    {runningProgram.steps[currentStep].frequency_icon}
                  </div>
                  <div className="current-step-info">
                    <div className="current-step-name">{runningProgram.steps[currentStep].frequency_name}</div>
                    <div className="current-step-time">{formatTime(timeRemaining)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!runningProgram && (
          <>
            <div className="programs-header">
              <h1 className="programs-title">{t('programs.title')}</h1>
              <p className="programs-subtitle">{t('programs.subtitle')}</p>
              <div className="category-filter">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    className={`category-btn ${selectedCategory === cat ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(cat)}
                    data-testid={`category-${cat}`}
                  >
                    {cat === 'alle' ? `📚 ${t('programs.categories.all')}` : translateCategory(cat)}
                  </button>
                ))}
              </div>
            </div>

            <div className="programs-grid">
              {filteredPrograms.map((program) => {
                const isPremiumProgram = program.is_premium;
                const userIsPremium = subscriptionStatus?.tier === 'premium';
                const isLocked = isPremiumProgram && !userIsPremium;
                
                return (
                  <div 
                    key={program.id} 
                    className={`program-card ${isLocked ? 'locked' : ''}`}
                    data-testid={`program-${program.id}`}
                  >
                    {isPremiumProgram && (
                      <div className="premium-badge-program">💎 Premium</div>
                    )}
                    
                    <div className="program-header">
                      <div className="program-icon">{program.icon}</div>
                      <div className="program-category-badge">{translateCategory(program.category)}</div>
                    </div>

                    <h3 className="program-name">{translateProgramName(program.name)}</h3>
                    <p className="program-description">{translateProgramDescription(program)}</p>

                    <div className="program-steps-preview">
                      {program.steps.map((step, index) => (
                        <React.Fragment key={index}>
                          <div className="step-preview">
                            <span className="step-preview-icon" style={{ color: step.frequency_color }}>
                              {step.frequency_icon}
                            </span>
                            <span className="step-preview-duration">{step.duration_minutes}'</span>
                          </div>
                          {index < program.steps.length - 1 && (
                            <span className="step-arrow">→</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    <div className="program-footer">
                      <div className="program-total-time">
                        ⏱️ {getTotalDuration(program)} {t('programs.minutes')}
                      </div>
                      <button
                        onClick={() => handleProgramClick(program)}
                        className={`btn-start-program ${isLocked ? 'locked' : ''}`}
                        data-testid={`start-program-${program.id}`}
                      >
                        {isLocked ? '🔒 Premium' : `▶️ ${t('programs.start')}`}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
      
      {/* Premium Upgrade Modal */}
      {showPremiumModal && (
        <div className="custom-dialog-overlay" onClick={() => setShowPremiumModal(false)}>
          <div className="custom-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-icon">💎</div>
            <h3 className="dialog-title">{t('programs.premiumProgram')}</h3>
            <p className="dialog-message">
              {t('programs.premiumDescription')}
            </p>
            <div className="dialog-actions">
              <button 
                onClick={() => setShowPremiumModal(false)}
                className="btn-dialog-cancel"
              >
                {t('common.cancel')}
              </button>
              <button 
                onClick={() => {
                  setShowPremiumModal(false);
                  navigate('/pricing');
                }}
                className="btn-dialog-confirm"
              >
                {t('programs.upgradeNow')}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}

export default ProgramsView;
